import { renderBlocks } from '@/components/blocks';
import { Layout } from '@/components/layout/Layout';
import { BASE_REVALIDATE } from '@/lib/constants';
import { useDynamicYield } from '@/lib/dynamicYield';
import {
  allInfoPagesQuery,
  infoPageQuery,
  latestPostsQuery,
  navigationQuery,
  navigations_exQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import {
  buildLayoutProp,
  getValidLocale,
  queryParametersWithAudience,
} from '@/lib/utils';

import type { SanityNavigationType } from '@/types/sanity';
import type { Navigation_exType, PageData, SiteSettings } from '@/types/shared';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';

export const getStaticPaths = (async () => {
  const client = getClient();
  const allInfoPages = await client.fetch(allInfoPagesQuery());

  const paths = allInfoPages.map((post) => ({
    params: { slug: post.slug.current.split('/') },
    locale: post._lang,
  }));

  return { paths, fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({
  params: { slug },
  locale,
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }
  const navigations_ex = await client.fetch<Navigation_exType[]>(
    navigations_exQuery({ locale: validLocale }),
  );

  const infoPage = await client.fetch<PageData>(
    infoPageQuery({ ...queryParametersWithAudience(slug, navigations_ex) }),
  );

  if (!infoPage) {
    return {
      notFound: true,
    };
  }

  const siteSettings = await client.fetch<SiteSettings>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const latestPostsData = await client.fetch(
    latestPostsQuery({ locale: validLocale }),
  );

  const navigation = await client.fetch<SanityNavigationType>(
    navigationQuery({
      locale: validLocale,
    }),
  );

  return {
    props: {
      infoPage,
      slug,
      siteSettings,
      locale: validLocale,
      draftMode,
      latestPostsData,
      navigation,
      navigations_ex,
      token: draftMode ? readToken : '',
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const InfoPage = ({
  infoPage,
  siteSettings,
  locale,
  draftMode,
  latestPostsData,
  navigation,
  navigations_ex,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { recommendationContext } = useDynamicYield(locale, 'OTHER');
  return (
    <Layout
      SEO={infoPage?.SEO}
      siteSettings={siteSettings}
      preview={draftMode}
      navigation={navigation}
      navigations_ex={navigations_ex}
      recommendationContext={recommendationContext}
      audienceRef={infoPage?.audienceRef}
      layout={buildLayoutProp(infoPage)}
    >
      {renderBlocks(infoPage?.blocks, {
        latestPostsData,
        pageAudience: infoPage?.audienceRef,
        isAudienceSwitcherEnabled: siteSettings?.isAudienceSwitcherEnabled,
      })}
    </Layout>
  );
};

export default InfoPage;
